import {
    Terminal,
    FileEdit,
    Globe,
    Puzzle,
    CheckCircle2,
    GitBranch,
    Code2,
    Clock,
    FolderTree,
    MessageSquare,
    Keyboard,
    Workflow
} from 'lucide-react'

export default function Features() {
	return (
		<div className="w-full">
			{/* Header */}
			<section className="bg-vscode-editor border-b border-vscode-border py-20">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h1 className="text-5xl md:text-6xl font-semibold text-vscode-text-bright mb-6 font-sans">
						All Features
					</h1>
					<p className="text-xl text-vscode-text-muted max-w-3xl mx-auto font-mono">
						Explore everything SakaDev can do to supercharge your development workflow
					</p>
				</div>
			</section>

			{/* Core Features */}
			<section className="py-20 bg-vscode-bg">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-semibold text-vscode-text-bright mb-12 font-sans">Core Capabilities</h2>
					<div className="grid md:grid-cols-2 gap-8">
						<DetailedFeatureCard
							icon={<Terminal className="w-10 h-10" />}
							title="Terminal Command Execution"
							description="Execute any CLI command directly from SakaDev. Install packages, run builds, start dev servers, manage databases—all with intelligent approval and monitoring."
							features={[
								"Background process support",
								"Real-time output monitoring",
								"Interactive command handling",
								"Multi-terminal management"
							]}
						/>
						<DetailedFeatureCard
							icon={<FileEdit className="w-10 h-10" />}
							title="Smart File Operations"
							description="Create, edit, and manage files with intelligent diff views and timeline tracking. SakaDev monitors linter errors and fixes issues proactively."
							features={[
								"Intelligent diff viewer",
								"Timeline change tracking",
								"Auto-fix linter errors",
								"Syntax error detection"
							]}
						/>
						<DetailedFeatureCard
							icon={<Globe className="w-10 h-10" />}
							title="Browser Automation"
							description="Launch headless browsers for testing and debugging. Click, type, scroll, and capture screenshots for comprehensive visual testing."
							features={[
								"Visual bug detection",
								"End-to-end testing",
								"Screenshot capture",
								"Console log monitoring"
							]}
						/>
						<DetailedFeatureCard
							icon={<Puzzle className="w-10 h-10" />}
							title="Model Context Protocol (MCP)"
							description="Extend SakaDev with custom tools and integrations. Connect to APIs, databases, or build domain-specific capabilities."
							features={[
								"Custom tool creation",
								"Community MCP servers",
								"API integrations",
								"Database connections"
							]}
						/>
					</div>
				</div>
			</section>

			{/* Productivity Features */}
			<section className="py-20 bg-vscode-sidebar border-t border-vscode-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-semibold text-vscode-text-bright mb-12 font-sans">Productivity Boosters</h2>
					<div className="grid md:grid-cols-3 gap-8">
						<FeatureCard
							icon={<CheckCircle2 className="w-8 h-8" />}
							title="Checkpoints & Restore"
							description="Save workspace snapshots at each step. Compare changes and restore to any previous state instantly."
						/>
						<FeatureCard
							icon={<Clock className="w-8 h-8" />}
							title="Auto-Approve"
							description="Speed up workflow with auto-approval for read operations. Customize approval rules per project."
						/>
						<FeatureCard
							icon={<FolderTree className="w-8 h-8" />}
							title="@ Mentions"
							description="Add context with @file, @folder, @url, @problems, @git, and @terminal mentions for precise control."
						/>
						<FeatureCard
							icon={<MessageSquare className="w-8 h-8" />}
							title="Slash Commands"
							description="Quick actions with /new-task, /new-rule, /smol, /report-bug, and custom workflow commands."
						/>
						<FeatureCard
							icon={<Keyboard className="w-8 h-8" />}
							title="Keyboard Shortcuts"
							description="Fully customizable shortcuts for adding code to chat, focusing input, and triggering commands."
						/>
						<FeatureCard
							icon={<Workflow className="w-8 h-8" />}
							title="SakaDevRules"
							description="Define project-specific rules and workflows that SakaDev automatically follows."
						/>
					</div>
				</div>
			</section>

			{/* Advanced Features */}
			<section className="py-20 bg-vscode-bg border-t border-vscode-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-semibold text-vscode-text-bright mb-12 font-sans">Advanced Capabilities</h2>
					<div className="grid md:grid-cols-2 gap-8">
						<DetailedFeatureCard
							icon={<Code2 className="w-10 h-10" />}
							title="Intelligent Context Management"
							description="Smart codebase analysis that maintains context awareness for large projects without overwhelming the AI model."
							features={[
								"AST-based code analysis",
								"Regex search capabilities",
								"Selective file reading",
								"Context window optimization"
							]}
						/>
						<DetailedFeatureCard
							icon={<GitBranch className="w-10 h-10" />}
							title="Git Integration"
							description="Seamless version control with AI-generated commit messages, change review, and branch management."
							features={[
								"Auto commit messages",
								"Change diff review",
								"Branch operations",
								"Merge assistance"
							]}
						/>
					</div>
				</div>
			</section>

			{/* CLI Features */}
			<section className="py-20 bg-vscode-sidebar border-t border-vscode-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="max-w-4xl mx-auto text-center space-y-8">
						<div className="inline-block p-4 rounded bg-vscode-accent/20">
							<Terminal className="w-12 h-12 text-vscode-accent" />
						</div>
						<h2 className="text-3xl font-semibold text-vscode-text-bright font-sans">
							SakaDev CLI
						</h2>
						<p className="text-xl text-vscode-text-muted font-mono">
							Run SakaDev from your terminal with full scripting support. Perfect for CI/CD pipelines and automation.
						</p>
						<div className="bg-vscode-editor border border-vscode-border rounded p-6 text-left">
							<code className="text-green-400 font-mono">
								$ cat README.md | sakadev "Summarize this for me"
								<br />
								$ sakadev "Fix all TypeScript errors in the project"
								<br />
								$ sakadev task create "Add authentication"
							</code>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}

function DetailedFeatureCard({ 
	icon, 
	title, 
	description, 
	features 
}: { 
	icon: React.ReactNode
	title: string
	description: string
	features: string[]
}) {
		return (
		<div className="p-8 bg-vscode-sidebar rounded border border-vscode-border hover:border-vscode-accent transition-colors">
			<div className="text-vscode-accent mb-4">{icon}</div>
			<h3 className="text-2xl font-semibold text-vscode-text-bright mb-3 font-sans">{title}</h3>
			<p className="text-vscode-text mb-6 font-mono text-sm">{description}</p>
			<ul className="space-y-2">
				{features.map((feature) => (
					<li key={feature} className="flex items-start space-x-2">
						<CheckCircle2 className="w-5 h-5 text-vscode-accent flex-shrink-0 mt-0.5" />
						<span className="text-vscode-text-muted font-mono text-sm">{feature}</span>
					</li>
				))}
			</ul>
		</div>
	)
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
	return (
		<div className="p-6 bg-vscode-editor rounded border border-vscode-border hover:border-vscode-accent transition-colors">
			<div className="text-vscode-accent mb-4">{icon}</div>
			<h3 className="text-xl font-semibold text-vscode-text-bright mb-2 font-sans">{title}</h3>
			<p className="text-vscode-text font-mono text-sm">{description}</p>
		</div>
	)
}
